import React, {useRef} from 'react';

import {HighlightableTextWrapper} from 'app/comments/components/HighlightableTextWrapper';
import {ClickToEditButton} from 'app/components/general/ClickToEditButton';
import {StartDocumentButton} from 'app/components/general/StartDocumentPopover';
import {DocumentFoldersList} from 'app/folders/components/DocumentFoldersList';
import {DocHeaderStructure} from 'app/general/structures/DocHeader';
import {DocumentMetadataStatusButtons} from 'app/general/structures/DocumentMetadataStatusButtons';
import {
  HIGHLIGHT_PROCEDURE_DESCRIPTION_STEPID,
  HIGHLIGHT_PROCEDURE_TITLE_STEPID,
} from 'app/general/structures/UserContentContainer/utils/highlightUtils';
import {ImagesCarousel} from 'app/general/widgets/ImagesCarousel';
import {DocumentPolicyList} from 'app/policies/components/DocumentPolicyList';
import {ProcedureStepFiles} from 'app/procedures/components/ProcedureStepFiles';
import {DocumentTagsList} from 'app/tags/components/DocumentTagsList';
import {getProcedureName} from 'app/utils/fallbacks';
import {isRightToLeftWrapper} from 'app/utils/rtl';

import {ManagersList} from './ManagersList';
import {ModifiedCreatedDataStamp} from './ModifiedCreatedDataStamp';

import type {VersionDiffType} from 'app/components/VersionDiffer';
import type {ProcedureHashidSerializedType} from 'sweet-types';

interface Props {
  content: Pick<VersionDiffType, 'name' | 'description' | 'thumbnail'>;
  procedure: ProcedureHashidSerializedType;
  warningComponent?: JSX.Element;
  advancedModeEnabled?: boolean;
  editableEmptyDataMessage?: string;
  procedureAndUserInSameAccount?: boolean;
  editMode?: boolean;
  versionId: number;
}

export function ViewProcedureMetaData({
  content,
  procedure,
  versionId,
  editableEmptyDataMessage,
  advancedModeEnabled,
  procedureAndUserInSameAccount,
  warningComponent,
  editMode,
}: Props) {
  const descriptionContentRef = useRef<HTMLDivElement | null>(null);
  const titleRef = useRef<HTMLDivElement | null>(null);

  const hasNoTagsOrDescription =
    procedure.tags.length === 0 && content.description.content === '';
  const procedureTitle =
    typeof content.name === 'string'
      ? getProcedureName(content, true)
      : content.name;
  const embeds = [...(content.description.embeds ?? [])];
  const carousel = [...content.description.images, ...embeds];
  return (
    <DocHeaderStructure
      thumbnail={content.thumbnail}
      warningContent={warningComponent}
      advancedContent={
        <div className="row">
          {advancedModeEnabled === true ? (
            <div className="col">
              <ModifiedCreatedDataStamp document={procedure} />
              {procedure.managers != null ? (
                <ManagersList document={procedure} />
              ) : null}
            </div>
          ) : null}
          <div className="col-auto ml-auto text-muted d-flex align-items-center mb-1">
            <DocumentMetadataStatusButtons
              canEdit={procedure.can_edit}
              contentType="procedure"
              context={'view'}
              document={procedure}
            />
          </div>
        </div>
      }
      titleContent={
        <div
          className={
            isRightToLeftWrapper(content.name) ? 'right-to-left-text' : ''
          }
        >
          <h1 role="heading" aria-level={1}>
            {editMode ? (
              <>
                {procedureTitle}
                <ClickToEditButton />
              </>
            ) : (
              <div ref={titleRef}>
                <HighlightableTextWrapper
                  objectId={procedure.id}
                  versionId={versionId}
                  stepId={HIGHLIGHT_PROCEDURE_TITLE_STEPID}
                  content={procedureTitle}
                  contentType={'procedureversion'}
                  innerRef={titleRef}
                />
              </div>
            )}
          </h1>
          {procedureAndUserInSameAccount && (
            <span
              className={`d-print-none ${
                isRightToLeftWrapper(content.name)
                  ? 'float-left'
                  : 'float-right'
              } mr-1 ml-1`}
            >
              <StartDocumentButton
                objectId={procedure.id}
                contentType="procedure"
              />
            </span>
          )}
        </div>
      }
      tagsContent={
        <>
          {hasNoTagsOrDescription && editableEmptyDataMessage != null ? (
            <div className="form-group">
              <span className="small text-secondary font-weight-light">
                {editableEmptyDataMessage}
              </span>
            </div>
          ) : (
            <DocumentTagsList tags={procedure.tags} />
          )}
          <DocumentFoldersList connections={procedure.connections} />
        </>
      }
      policyContent={<DocumentPolicyList policies={procedure.policies} />}
      descriptionContent={
        <div ref={descriptionContentRef}>
          <HighlightableTextWrapper
            objectId={procedure.id}
            versionId={versionId}
            stepId={HIGHLIGHT_PROCEDURE_DESCRIPTION_STEPID}
            content={content.description.content}
            contentType={'procedureversion'}
            innerRef={descriptionContentRef}
          />
          {carousel.length > 0 ? (
            <div onClick={stopPropagation}>
              <ImagesCarousel images={carousel} className="step-images" />
            </div>
          ) : null}
          {content.description.files?.length > 0 ? (
            <div className="mt-4" onClick={stopPropagation}>
              <ProcedureStepFiles files={content.description.files} />
            </div>
          ) : null}
        </div>
      }
    />
  );
}

function stopPropagation(e: React.MouseEvent<HTMLDivElement>) {
  e.stopPropagation();
}
